// src/components/LookupBase.js
import React, { useEffect, useRef, useState } from "react";
import * as FS from "../styles/formStyles";
import { getJwt } from "../utils/auth";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Props summary:
 * - endpoint (string) : "/api/umats"
 * - queryBuilder(search) => query string (starting with ?), encoded
 * - renderItem(item, { selected }) => jsx row
 * - renderChip(item, onDelete) => jsx chip
 * - value, onChange (controlled) or initialSelected (uncontrolled)
 * - multiple (bool)
 * - placeholder
 */
export default function LookupBase({
  endpoint,
  queryBuilder,
  renderItem,
  renderChip,
  value,
  onChange,
  initialSelected = null,
  multiple = false,
  placeholder = "Ketik untuk mencari...",
  autoFocus = false,
}) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // selected state (controlled/uncontrolled)
  const [selected, setSelected] = useState(value !== undefined ? value : initialSelected);

  const jwt = getJwt();
  const headers = jwt ? { Authorization: `Bearer ${jwt}` } : {};

  useEffect(() => { if (value !== undefined) setSelected(value); }, [value]);
  useEffect(() => { onChange?.(selected); }, [selected]);

  const inputRef = useRef();

  // fetch results when q changes (debounced)
  useEffect(() => {
    if (!q) { setItems([]); return; }
    let mounted = true;
    const t = setTimeout(async () => {
      setLoading(true); setErr(null);
      try {
        const query = queryBuilder ? queryBuilder(q) : `?filters%5B$or%5D%5B0%5D%5Bname%5D%5B$containsi%5D=${encodeURIComponent(q)}&populate=*`;
        const url = `${API_URL}${endpoint}${query}`;
        const resp = await fetch(url, { headers });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        // normalize typical Strapi shapes
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data?.results) ? data.results : data?.data ? (Array.isArray(data.data) ? data.data : [data.data]) : [];
        const normalized = list.map(d => (d?.attributes ? { documentId: d.documentId, id: d.id, ...d.attributes } : d));
        if (mounted) setItems(normalized);
      } catch (e) {
        console.error(e);
        setErr(String(e?.message ?? e));
      } finally {
        if (mounted) setLoading(false);
      }
    }, 180);
    return () => { mounted = false; clearTimeout(t); };
  }, [q, endpoint, queryBuilder]);

  // helper check
  function isSelectedItem(item) {
    if (!selected) return false;
    if (multiple) return Array.isArray(selected) && selected.some(s => s?.id === item?.id);
    return selected?.id === item?.id;
  }

  function addSelect(item) {
    if (multiple) {
      const arr = Array.isArray(selected) ? [...selected] : [];
      if (!arr.some(s => s?.id === item?.id)) {
        const next = [...arr, item];
        if (value === undefined) setSelected(next);
        onChange?.(next);
      }
      // reset search so user can add more
      setQ("");
      inputRef.current?.focus();
    } else {
      // single select: set selected, reset q, and disable input (readonly)
      if (value === undefined) setSelected(item);
      onChange?.(item);
      setQ("");
      // do not auto-focus input — leave it readonly until cleared
    }
  }

  function removeSelect(item) {
    if (multiple) {
      const next = (Array.isArray(selected) ? selected : []).filter(s => s?.id !== item?.id);
      if (value === undefined) setSelected(next);
      onChange?.(next);
      // keep focus on input
      inputRef.current?.focus();
    } else {
      if (value === undefined) setSelected(null);
      onChange?.(null);
      // allow searching again
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  // make selected items appear on top of list (for visibility)
  //const selectedMap = Array.isArray(selected) ? selected : (selected ? [selected] : []);
  //const filteredItems = [
  //  ...selectedMap.filter(s => !!s),
  //  ...items.filter(it => !isSelectedItem(it))
  //];
  
  // results should NOT include already selected items
  const filteredItems = Array.isArray(items) ? items.filter(it => !isSelectedItem(it)) : [];


  return (
    <div style={{ maxWidth: 720 }}>
      {/* bordered area that contains chips + input */}
      <div style={{ border: "1px solid #cfcfcf", padding: 8, borderRadius: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {/* render chips inside the same bordered box */}
          {multiple
            ? (Array.isArray(selected) ? selected.map(it => (
                <div key={it.id} style={FS.chip}>
                  {renderChip ? renderChip(it, () => removeSelect(it)) : <DefaultChip item={it} onDelete={() => removeSelect(it)} />}
                </div>
              )) : null)
            : selected
              ? <div style={FS.chip}>{ renderChip ? renderChip(selected, () => removeSelect(selected)) : <DefaultChip item={selected} onDelete={() => removeSelect(selected)} /> }</div>
              : null}
        </div>

        <div style={{ marginTop: 8 }}>
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={ (!multiple && selected) ? "Hapus pilihan untuk mencari lagi..." : placeholder }
            style={{ ...FS.input, ...( (!multiple && selected) ? { background: "#f3f4f6", cursor: "not-allowed" } : {} ) }}
            readOnly={!multiple && !!selected} // single select becomes readonly when selected
            autoFocus={autoFocus}
          />
        </div>
      </div>

      {/* results list */}
      <div style={{ marginTop: 8 }}>
        <div style={FS.choiceList}>
          {loading && <div style={{ padding: 12 }}>Loading...</div>}
          {err && <div style={{ padding: 12, color: "red" }}>Error: {err}</div>}
          {!loading && filteredItems.length === 0 && q !== "" && <div style={{ padding: 12, color: "#6b7280" }}>Tidak ada hasil</div>}
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {filteredItems.map((item, idx) => {
              const already = isSelectedItem(item);
              const itemStyle = {
                ...FS.choiceItem,
                ...(already ? FS.choiceItemSelected : {}),
                borderBottom: idx === filteredItems.length - 1 ? "none" : FS.choiceItem.borderBottom,
              };
              return (
                <li key={item.id ?? JSON.stringify(item)} style={itemStyle} onClick={() => !already && addSelect(item)}>
                  {renderItem ? renderItem(item, { selected: already }) : <DefaultItem item={item} />}
                  {already && <div style={{ marginLeft: "auto", color: "#2b4a84", fontSize: 13, fontWeight: 600 }}>Selected</div>}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* default renderers */
function DefaultItem({ item }) {
  return (
    <>
      <div style={FS.choiceAvatar}>{(item.fullName || item.name || item.subject || "U")[0]}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{item.fullName ?? item.name ?? item.subject ?? "—"}</div>
        <div style={{ fontSize: 13, color: "#6b7280" }}>{item.kodeUmat ?? item.email ?? ""}</div>
      </div>
    </>
  );
}

function DefaultChip({ item, onDelete }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <div style={{ fontSize: 13 }}>{item.fullName ?? item.name ?? item.subject ?? "—"}</div>
      <button onClick={onDelete} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#6b7280" }} aria-label="remove">×</button>
    </div>
  );
}
